import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FiX, FiMail, FiClock, FiCheckCircle } from "react-icons/fi";
import { MdOutlineVerifiedUser } from "react-icons/md";

function EmailVerification({ data, onClose, generatedOtp, onVerified, zIndex }) {
  const [otp, setOtp] = useState("");
  const [enteredOtp, setEnteredOtp] = useState("");
  const [timer, setTimer] = useState(300);
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const inputRefs = useRef([]);

  useEffect(() => {
    setOtp(generatedOtp);
    startTimer();

    const emailData = {
      otp: generatedOtp,
      companyName: (data && data.companyName) || "",
      email: (data && data.email) || "",
      mobileNum: (data && data.mobNo) || "",
    };

    sendOtpEmail(emailData); // Send the initial OTP email

    // Cleanup timer on unmount
    return () => clearInterval(timerInterval.current);
  }, [data]);

  // Timer logic
  const timerInterval = useRef(null);

  const startTimer = () => {
    setTimer(300);
    if (timerInterval.current) {
      clearInterval(timerInterval.current);
    }

    timerInterval.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerInterval.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const sendOtpEmail = (emailData) => {
    axios
      .post(`${apiBaseUrl}/main/sendVerfMail`, emailData)
      .then((response) => {
        //////////console.log("OTP email sent successfully:", response.data);
      })
      .catch((error) => {
        console.error("Failed to send OTP email:", error);
      });
  };

  const handleOtpChange = (event, index) => {
    const value = event.target.value;

    if (/^\d*$/.test(value)) {
      const newOtp = enteredOtp.split("");
      newOtp[index] = value;
      const updatedOtpStr = newOtp.join("");
      setEnteredOtp(updatedOtpStr);

      if (value && index < inputRefs.current.length - 1) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Backspace" && !event.target.value && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData("Text");
    const numericData = pastedData.replace(/\D/g, "").slice(0, 6);

    setEnteredOtp(numericData);

    inputRefs.current.forEach((input, idx) => {
      if (input) {
        input.value = numericData[idx] || "";
      }
    });

    const focusIndex = Math.min(numericData.length, 5);
    if (inputRefs.current[focusIndex]) {
      inputRefs.current[focusIndex].focus();
    }
  };

  const verifyOtp = () => {
    if (enteredOtp === otp.toString()) {
      Swal.fire({
        icon: "success",
        title: "Verified!",
        text: "Email verified successfully.",
        timer: 1000,
        showConfirmButton: false,
      });
      onVerified(true);
      onClose();
    } else {
      Swal.fire({
        icon: "error",
        title: "Verification Failed",
        text: "OTP does not match or has expired.",
        timer: 1000,
        showConfirmButton: false,
      });
      onVerified(false);
    }
  };

  const resendOtp = () => {
    const newGeneratedOtp = Math.floor(100000 + Math.random() * 900000);
    setOtp(newGeneratedOtp); // Update the state with the new OTP
    setEnteredOtp(""); // Clear the entered OTP
    
    // Clear all inputs manually
    inputRefs.current.forEach((input) => {
      if (input) input.value = "";
    });
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }

    startTimer(); // Restart the timer

    const emailData = {
      otp: newGeneratedOtp,
      companyName: (data && data.companyName) || "",
      email: (data && data.email) || "",
      mobileNum: (data && data.mobNo) || "",
    };

    sendOtpEmail(emailData); // Send the new OTP email
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 transition-all duration-300"
      style={{ zIndex: zIndex || 10000 }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[420px] overflow-hidden transform scale-100 transition-transform relative border border-slate-100 flex flex-col font-inter">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-white">
              <MdOutlineVerifiedUser className="text-lg" />
            </div>
            <span className="font-semibold text-sm tracking-wide font-inter">
              Enter Verification Code
            </span>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 transition-colors border-none cursor-pointer text-white"
          >
            <FiX className="text-base" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="px-5 pt-3 pb-4 flex flex-col gap-2">
          <div className="flex flex-col gap-0.5">
            <h4 className="text-xs font-semibold text-slate-700 font-inter">
              We sent a verification code to your email.
            </h4>
            <span className="text-[10px] text-slate-400 font-inter leading-relaxed">
              Enter the 6-digit code below. Be careful not to share it with anyone.
            </span>
            <div className="bg-slate-50 border border-slate-100 rounded-lg px-3.5 py-1.5 flex items-center gap-2 mt-1">
              <FiMail className="text-blue-500 text-xs" />
              <span className="text-[11px] text-slate-500 font-inter font-medium">
                Sent to: <span className="text-slate-700 font-semibold">{(data && data.email) || "admin@example.com"}</span>
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] font-bold tracking-wider text-slate-500 font-inter uppercase">
              Verification Code
            </label>
            {/* OTP Input Fields */}
            <div className="flex justify-between gap-1.5 my-0.5">
              {Array.from({ length: 6 }).map((_, index) => (
                <input
                  key={index}
                  type="text"
                  className="w-9 h-9 text-center text-base font-bold text-slate-800 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-inter"
                  maxLength="1"
                  placeholder="•"
                  onChange={(e) => handleOtpChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  ref={(el) => (inputRefs.current[index] = el)}
                />
              ))}
            </div>
          </div>

          {/* Timer displaying in rounded card */}
          {timer > 0 && (
            <div className="bg-slate-50 border border-slate-100 rounded-lg px-3.5 py-2 flex flex-col gap-0.5">
              <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-600 font-inter">
                <FiClock className="text-amber-500 text-xs" />
                <span>
                  Resend OTP in <span className="text-blue-600">{timer}s</span>
                </span>
              </div>
              <span className="text-[10px] text-slate-400 font-inter">
                Did not receive the code? Check your spam folder or request a new one.
              </span>
            </div>
          )}

          {/* Action Footer */}
          <div className="flex items-center gap-2.5 pt-0.5">
            <button
              onClick={onClose}
              className="flex-1 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 bg-white hover:bg-slate-50 border border-slate-200 active:bg-slate-100 rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer font-inter"
            >
              <FiX className="text-xs" />
              Cancel
            </button>
            {timer > 0 ? (
              <button
                onClick={verifyOtp}
                className="flex-1 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-lg shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-1.5 border-none cursor-pointer font-inter"
              >
                <FiCheckCircle className="text-xs" />
                Verify
              </button>
            ) : (
              <button
                onClick={resendOtp}
                className="flex-1 py-2 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 rounded-lg shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-1.5 border-none cursor-pointer font-inter"
              >
                <FiCheckCircle className="text-xs" />
                Resend OTP
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailVerification;
